import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as moment from 'moment';
import classNames from 'classnames';
import { IMaskInput } from 'react-imask';
import { useDispatch } from 'react-redux';
import { Button, FormFeedback, Spinner } from 'reactstrap';
import { isInvalid } from '../../helpers/functions';
import { confirmTwoFactorCode, resendTwoFactorCode } from '../../slices/thunks';

const RESEND_COOLDOWN_MS = 60 * 1000;

const TwoFactorCodeStep = ({ pendingToken, onSuccess, t }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(RESEND_COOLDOWN_MS);

  useEffect(() => {
    if (canResend || cooldownRemaining <= 0) {
      setCanResend(true);
      return;
    }

    const interval = setInterval(() => setCooldownRemaining((ms) => ms - 1000), 1000);
    return () => clearInterval(interval);
  }, [canResend, cooldownRemaining]);

  const handleResend = async () => {
    if (!canResend) return;
    await dispatch(resendTwoFactorCode(pendingToken));
    setCooldownRemaining(RESEND_COOLDOWN_MS);
    setCanResend(false);
  };

  const validation = useFormik({
    initialValues: { code: '' },
    validationSchema: Yup.object({
      code: Yup.string().required(t('validation-required-token')),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const result = await dispatch(confirmTwoFactorCode(pendingToken, values.code));
      setIsLoading(false);

      if (!result) {
        validation.setFieldError('code', t('validation-invalid-token'));
        return;
      }

      onSuccess(result);
    },
  });

  return (
    <div className="p-2 mt-4 text-center">
      <h5 className="text-primary mb-3">{t('2fa-login-title')}</h5>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <div className="mb-3">
          <IMaskInput
            mask="000000"
            unmask={true}
            name="code"
            type="text"
            placeholder="••••••"
            className={classNames('form-control text-center fs-3', {
              'is-invalid': isInvalid(validation, 'code'),
            })}
            style={{ letterSpacing: '.5rem' }}
            onAccept={(value, mask) => validation.setFieldValue('code', mask.value)}
            value={validation.values.code || ''}
            inputRef={(el) => {
              if (el) el.addEventListener('blur', validation.handleBlur);
            }}
          />
          {isInvalid(validation, 'code') && (
            <FormFeedback type="invalid">{validation.errors.code}</FormFeedback>
          )}
        </div>

        <div className="text-center pt-1 mb-3">
          {!canResend ? (
            <span className="text-muted">
              {t('two-factor-resend-code')} ({moment.utc(cooldownRemaining).format('mm:ss')})
            </span>
          ) : (
            <Button color="link" onClick={handleResend} type="button">
              {t('two-factor-resend-code')}
            </Button>
          )}
        </div>

        <Button color="success" className="w-100" type="submit" disabled={isLoading}>
          {isLoading && <Spinner size="sm" className="me-2" />}
          {t('signin')}
        </Button>
      </form>
    </div>
  );
};

export default TwoFactorCodeStep;
